import React, { useState, useEffect, useRef, useCallback } from "react";
import { uniq, each, size, takeRight, isEqual } from "lodash";
import { FundTag } from "./fund_tag";
import Tooltip from "frontend/src/components/Tooltip/Tooltip";


interface FundTagListProps {
  tags?: string[];
  truncateWithInitials?: number[];
}

interface TagRef {
  node?: HTMLElement;
}

export const FundTagList: React.FC<FundTagListProps> = ({
  tags = [],
  truncateWithInitials = [],
}) => {
  const [visibleTags, setVisibleTags] = useState<string[]>(tags);
  const nodeRef = useRef<HTMLDivElement>(null);
  const tagsRefsRef = useRef<{ [key: string]: TagRef }>({});

  const getContainerWidth = useCallback((): number => {
    if (nodeRef.current) {
      return nodeRef.current.clientWidth;
    }
    return 0;
  }, []);

  const getTagWidth = useCallback((tag: string): number => {
    const tagRef = tagsRefsRef.current[tag];
    if (!tagRef || !tagRef.node) {
      return 0;
    }

    // Get computed styles for margin
    const computedStyle = window.getComputedStyle(tagRef.node);
    const marginLeft = parseFloat(computedStyle.marginLeft) || 0;
    const marginRight = parseFloat(computedStyle.marginRight) || 0;

    // Get element width including padding and border, then add margins
    return tagRef.node.offsetWidth + marginLeft + marginRight;
  }, []);

  const getVisibleTags = useCallback((): string[] => {
    const tooltipTriggerWidth = 35;
    const containerWidth = getContainerWidth() - tooltipTriggerWidth;

    const visibleTagsList: string[] = [];
    let occupiedWidth = 0;

    each(tags, tag => {
      occupiedWidth += getTagWidth(tag);
      if (occupiedWidth < containerWidth) {
        visibleTagsList.push(tag);
      }
    });

    return visibleTagsList;
  }, [tags, getContainerWidth, getTagWidth]);

  const setVisibleTagsWithCalculation = useCallback(() => {
    setVisibleTags(tags);
    // Use setTimeout to ensure DOM is updated before calculating
    setTimeout(() => {
      const calculatedVisibleTags = getVisibleTags();
      setVisibleTags(calculatedVisibleTags);
    }, 0);
  }, [tags, getVisibleTags]);

  useEffect(() => {
    setVisibleTagsWithCalculation();
  }, [setVisibleTagsWithCalculation]);

  useEffect(() => {
    if (!isEqual(visibleTags, tags)) {
      setVisibleTagsWithCalculation();
    }
  }, [tags, setVisibleTagsWithCalculation]);

  const getHiddenTags = useCallback((): string[] => {
    const hiddenTagsCount = size(tags) - size(visibleTags);
    return takeRight(tags, hiddenTagsCount);
  }, [tags, visibleTags]);

  const saveRef = useCallback(
    (tag: string) => (ref: TagRef | null) => {
      if (!ref) {
        return null;
      }
      tagsRefsRef.current[tag] = ref;
      return ref;
    },
    []
  );

  const renderHiddenTags = (): React.ReactNode => {
    const hiddenTags = getHiddenTags();

    if (hiddenTags.length === 0) {
      return null;
    }

    const title = hiddenTags.join(", ");

    return (
      <Tooltip
        title={title}
        placement="top"
        overlayClassName="fund-tag-tooltip"
        align={{ useCssBottom: true }}
        overlayStyle={{ position: "fixed" }}
        destroyTooltipOnHide
      >
        <div className="ant-tag ant-tag--fund">...</div>
      </Tooltip>
    );
  };

  return (
    <div className="tile-tags" ref={nodeRef}>
      {uniq(visibleTags).map((tag, index) => (
        <FundTag
          title={tag}
          key={tag}
          ref={saveRef(tag)}
          truncateWithInitials={truncateWithInitials.indexOf(index) !== -1}
        />
      ))}

      {renderHiddenTags()}
    </div>
  );
};
